



































import Vue from 'vue'
import { Component, Prop, Emit, Watch } from 'vue-property-decorator'
import { showErrors, showNotification } from '@/utils/snack'
import { SnackType } from '@/interfaces/snack'
import AppDialog from '@/components/AppDialog.vue'
import AppSteps from '@/components/AppSteps.vue'
import ProducerFormInfo from '@/modules/Producer/components/forms/ProducerFormInfo.vue'
import ProducerFormCommission from '@/modules/Producer/components/forms/ProducerFormCommission.vue'
import { newProducer, editProducer } from '@/modules/Producer/gql/producer.gql'
import { IProducerInfoInput, IProducerCommissionInput } from '@/modules/Producer/interfaces/producer-form'
import { Producer } from '@/modules/Producer/interfaces/producer'
import { producerTypeKeys } from '@/modules/Producer/helpers/producer'
import { states } from '@/utils/us_states'
import delNonNumCode from '@/utils/delNonNumCode'

@Component({
  components: {
    ProducerFormInfo,
    AppDialog,
    ProducerFormCommission,
    AppSteps
  }
})
export default class ProducerForm extends Vue {
  @Prop(Boolean) value!: boolean
  @Prop(Number) readonly step!: number
  @Prop(String) readonly title!: string
  @Prop({ type: Object as () => Producer }) readonly producer!: Producer

  showDialog = false
  loading = false

  stepIndex = 0
  steps = [
    ['Information', 770, 731],
    ['Commission', 770, 301]
  ]

  infoInput: IProducerInfoInput = {
    name: '',
    typeIdx: -1,
    producerCode: '',
    partner: {},
    title: '',
    owedAmount: '',
    phone: '',
    email: '',
    producerSubCode: '',
    username: '',
    password: ''
  }

  commissionInput: IProducerCommissionInput = {
    newBusinessCommissionSplit: '',
    renewalCommissionSplit: ''
  }

  get stepItems () {
    return this.steps.map(st => st[0])
  }

  get showBackBtn () {
    return this.stepIndex > 0
  }

  get confirmText () {
    return this.stepIndex < this.steps.length - 1
      ? 'Next'
      : this.producer
        ? 'Save'
        : 'Create'
  }

  get confirmAheadText () {
    return this.producer ? 'Save' : 'Create'
  }

  get canPost () {
    return this.stepIndex < this.steps.length - 1
  }

  @Watch('value')
  onValueChanged (val: boolean) {
    if (val) {
      this.resetStep()
      this.initInput()
    }
    this.showDialog = val
  }

  resetStep () {
    this.stepIndex = this.step
  }

  initInput () {
    if (this.producer) {
      const {
        name, type,
        producerCode, partner,
        title, owedAmount, email, producerSubCode,
        phone, countryCallingCode, countryCode, extensionNumber, newBusinessCommissionSplit,
        renewalCommissionSplit,
        username
      } = this.producer

      this.infoInput = {
        name,
        typeIdx: producerTypeKeys.findIndex(key => key === type),
        producerCode,
        partner: {
          id: partner ? +partner.id : undefined,
          name: partner ? partner.name : ''
        },
        title,
        owedAmount,
        phone: (countryCode || '') + ',' + (phone || '') + ',' + (extensionNumber || '') + ',' + (countryCallingCode || ''),
        email,
        producerSubCode,
        username,
        password: ''
      }
      this.commissionInput = {
        newBusinessCommissionSplit,
        renewalCommissionSplit
      }
    } else {
      this.clearForm()
    }
  }

  handleStepClick (index: number) {
    if (!this.producer) {
      return
    }

    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    this.stepIndex = index
  }

  handleValidate () {
    const { stepIndex, steps } = this
    const refName = steps[stepIndex][0] as string
    return (this.$refs[refName] as Vue & {
      validate: () => null | { [key: string]: number | string }
    }).validate()
  }

  handleConfirm (event: Event, finishAhead = false) {
    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    if (this.stepIndex === this.steps.length - 1 || finishAhead) {
      this.loading = true
      if (this.producer) {
        this.handleUpdate()
      } else {
        this.handleCreate()
      }
    } else {
      this.stepIndex += 1
    }
  }

  handleBack () {
    if (this.stepIndex > 0) {
      this.stepIndex -= 1
    }
  }

  async handleCreate () {
    const {
      infoInput: {
        name, typeIdx,
        producerCode, partner: { id: partnerId },
        title, owedAmount, phone, email, producerSubCode, username, password
      },
      commissionInput: {
        newBusinessCommissionSplit,
        renewalCommissionSplit
      }
    } = this

    const phoneArr = phone.split(',')

    try {
      const res = await this.$apollo.mutate({
        mutation: newProducer,
        variables: {
          producerInput: {
            name,
            partnerId,
            type: producerTypeKeys[typeIdx] || null,
            producerCode,
            title,
            owedAmount,
            producerSubCode,
            renewalCommissionSplit,
            newBusinessCommissionSplit,
            email,
            phone: delNonNumCode(phoneArr[1] || ''),
            extensionNumber: phoneArr[2] || '',
            countryCode: phoneArr[0] || '',
            countryCallingCode: phoneArr[3] || '',
            username,
            password
          }
        }
      })
      this.clearForm()
      this.onSuccess('Create the new source successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  async handleUpdate () {
    const {
      infoInput: {
        name, typeIdx,
        producerCode, partner: { id: partnerId },
        title, owedAmount, phone, email, producerSubCode, username, password
      },
      commissionInput: {
        newBusinessCommissionSplit,
        renewalCommissionSplit
      }
    } = this

    const phoneArr = phone.split(',')

    try {
      const res = await this.$apollo.mutate({
        mutation: editProducer,
        variables: {
          producerId: +this.producer.id,
          producerInput: {
            name,
            partnerId,
            type: producerTypeKeys[typeIdx] || null,
            producerCode,
            title,
            owedAmount,
            producerSubCode,
            renewalCommissionSplit,
            newBusinessCommissionSplit,
            email,
            phone: delNonNumCode(phoneArr[1] || ''),
            extensionNumber: phoneArr[2] || '',
            countryCode: phoneArr[0] || '',
            countryCallingCode: phoneArr[3] || '',
            username,
            password
          }
        }
      })
      this.clearForm()
      this.onSuccess('Update producer successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  clearForm () {
    this.loading = false
    this.infoInput = {
      name: '',
      typeIdx: -1,
      producerCode: '',
      partner: {},
      title: '',
      owedAmount: '',
      phone: '',
      email: '',
      producerSubCode: '',
      username: '',
      password: ''
    }

    this.commissionInput = {
      newBusinessCommissionSplit: '',
      renewalCommissionSplit: ''
    }
  }

  @Emit('hide')
  handleHide () {
    this.$emit('input', false)
  }

  @Emit('cancel')
  handleCancel () {
    this.handleHide()
    this.clearForm()
  }

  @Emit('success')
  @Emit('hide')
  onSuccess (msg = '') {
    this.handleHide()
    this.resetStep()
    if (msg) {
      showNotification({
        msg,
        type: SnackType.Success
      })
    }
  }
}
